import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheck,
  FaChevronRight,
  FaClipboardList,
  FaFileMedical,
  FaPhone,
  FaPills,
  FaPaperPlane,
  FaStethoscope,
  FaTimes,
} from 'react-icons/fa';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import qs from 'query-string';
import { parseISO } from 'date-fns';
import { Card, Col, Row } from 'react-bootstrap';

import api from '../../services/api';
import './paciente-tele-atendimento.css';
import { useToast } from '../../hooks/toast';

import { useAuth } from '../../hooks/auth';
import { useVideoConference } from '../../hooks/videoConference';
import { useClinica } from '../../hooks/clinica';
import { ITelemedicina } from '../PacienteAtendimento';
import {
  formataCPF,
  formataIdade,
  formataTelefone,
  STATUS_TELEATENDIMENTO_STRING,
  STATUS_TELEATENDIMENTO,
} from '../../utils/funcoes';
import getResponseErrors from '../../utils/getResponseErrors';
import { IRetornoMudarStatus } from '../AgendaTeleAtendimento';

const requests = [
  {
    id: 'prescription',
    label: 'Prescrição médica',
    detail: 'Enviar receita para farmácia',
    icon: FaPills,
  },
  {
    id: 'exam',
    label: 'Solicitação de exame',
    detail: 'Orientar sobre coleta e agendamento',
    icon: FaFileMedical,
  },
  {
    id: 'referral',
    label: 'Encaminhamento',
    detail: 'Informar destino e documentos',
    icon: FaClipboardList,
  },
  {
    id: 'return',
    label: 'Retorno agendado',
    detail: 'Agendar próxima consulta',
    icon: FaCalendarAlt,
  },
];

interface IUrlParams {
  id: string;
}

interface IAgendamento {
  AgeCodigo: number;
  AgeConfCodigo: number;
  AgeMedUsuCodigo: number;
  AgeAteUsuCodigo: number;
  AgePacCodigo: number;
  AgeTipo: number;
  AgeObs: string;
  AgeInicio: string;
  AgeFim: string;
  AgeStatus: number;
  AgeFormaPag: number;
  AgeDataLiberacao: any;
  AgeEmail: any;
  AgePlano: boolean;
  AgeChave: string;
  AgePacStatus: number;
  AgePacStatusData: string;
  AgeClinica: number;
  AgeConfirmado: number;
  AgeOrigem: number;
  AgeTratamento: number;
  AgePagamentoMedico: number;
  AgeCheckup: number;
  AgeGuia: string;
  AgeConvCodigo: number;
  AgeTelemedicina: number;
  created_at: string;
  updated_at: string;
  statusTelemedicina: number;
  urlVideo: string;
}

const teleStatusConfig: {
  [key: number]: {
    bg: string;
    colorText: string;
    dot: string;
    title: string;
    text: string;
  };
} = {
  1: {
    bg: '#e2e3e5',
    colorText: '#383d41',
    dot: '#6c757d',
    title: 'Aguardando',
    text: 'Aguardando atendimento',
  },
  2: {
    bg: '#cce5ff',
    colorText: '#004085',
    dot: '#007bff',
    title: 'Encaminhar para atendimento',
    text:
      'Confirme se todas as orientações foram passadas ao paciente antes de encaminhar para atendimento médico.',
  },
  3: {
    bg: '#fff3cd',
    colorText: '#856404',
    dot: '#ffc107',
    title: 'Pendente',
    text: 'Atendimento pendente',
  },
  4: {
    bg: '#d4edda',
    colorText: '#14532d',
    dot: '#28a745',
    title: 'Concluído',
    text: 'Atendimento concluído',
  },
  5: {
    bg: '#e2d5f7',
    colorText: '#5b2c9f',
    dot: '#8031f7',
    title: 'Aguardando',
    text: 'Aguardando atendimento',
  },
  6: {
    bg: '#cff4fc',
    colorText: '#055160',
    dot: '#17a2b8',
    title: 'Finalizar atendimento',
    text:
      ' Confirme que todas as solicitações foram tratadas antes de encerrar.',
  },
  7: {
    bg: '#d1e7dd',
    colorText: '#0f5132',
    dot: '#198754',
    title: 'Pendente',
    text: 'Atendimento pendente',
  },
};

type IReturnAgendamento = IAgendamento & {
  agenda: IAgenda;
  procedimentos: IProcedimento[];
  paciente: IPaciente;
  atendimento: IAtendimento;
};
interface IAgenda {
  ConCodigo: number;
  ConTipo: number;
  ConUsuCodigo: number;
  ConNome: string;
  ConPerfil: string;
  ConPerfilCodigo: string;
  ConTempoPadrao: number;
  ConObs: string;
  ConAtende: boolean;
  ConEnviaEmail: boolean;
  ConDiasEmail: number;
  ConClinica: number;
  ConSemanal: number;
  ConIntegracao: number;
  ConAtivo: boolean;
  ConTratamento: boolean;
  ConPagamentoAntecipado: boolean;
  ConMedicoAgendaCompleta: boolean;
  ConOrdenaPorPagamento: boolean;
  ConOnline: boolean;
  created_at: string;
  updated_at: string;
}

export interface IProcedimento {
  ProCodigo: number;
  ProNome: string;
  ProCodigoAlvaro: string;
  ProValor: number;
  ProDuracao: number;
  perfil: Perfil;
}

export interface Perfil {
  PerCodigo: number;
  PerNome: string;
}

export interface IPaciente {
  PacCodigo: number;
  PacNome: string;
  PacNomeSocial: string;
  PacDataNasc: string;
  PacCPF: string;
  PacRG: string;
  PacCNPJ: string;
  PacIE: string;
  PacContato: string;
  PacTelefone: string;
  PacCelular: string;
  PacEmail: string;
  PacEmailValidado: boolean;
  PacEndereco: string;
  PacNumero: string;
  PacBairro: string;
  PacCidade: string;
  PacCidadeCodigo: string;
  PacCEP: string;
  PacComplemento: string;
  PacUF: string;
  PacDataCadastro: any;
  PacSexo: string;
  PacProfissao: any;
  PacEstCivil: any;
  PacEscolaridade: any;
  PacResponsavel: string;
  PacResponsavelCPF: string;
  PacResponsavelRG: any;
  PacAtivo: boolean;
  PacRestrito: boolean;
  PacFoto: any;
  PacChave: string;
  PacSenhaExame: string;
  PacPessoaFisica: boolean;
  PacClinica: number;
  PacOrigem: number;
  PacTag: string;
  created_at: string;
  updated_at: string;
  PacDataNascFormatada?: string;
  PacIdade?: string;
  avatar_url?: string;
}

export interface IAtendimento {
  AteCodigo: number;
  AteUsuCodigo: number;
  AteAgeCodigo: number;
  AteInicio: string;
  AteFim: string;
  AteTexto: string;
}

const PacienteTeleAtendimento: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [showNotice, setShowNotice] = useState(true);
  const [completed, setCompleted] = useState<string[]>([]);
  const [telemedicina, setTelemedicina] = useState<ITelemedicina>();
  const [loading, setLoading] = useState(false);
  const [agendamento, setAgendamento] = useState<IAgendamento | undefined>(
    undefined,
  );
  const [paciente, setPaciente] = useState<IPaciente | undefined>(undefined);

  const [agenda, setAgenda] = useState<IAgenda | undefined>(undefined);
  const [procedimentos, setProcedimentos] = useState<IProcedimento[]>([]);
  const [atendimento, setAtendimento] = useState<IAtendimento | undefined>(
    undefined,
  );

  function useQuery(): URLSearchParams {
    return new URLSearchParams(location.search);
  }

  const query = useQuery();
  const { params } = useRouteMatch<IUrlParams>();
  const { id } = params;
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const { user } = useAuth();
  const { setIsOpen, setUrl } = useVideoConference();

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const toggleRequest = (ids: string): void => {
    console.log('toggleRequest', ids);
    setCompleted(prev =>
      prev.includes(ids) ? prev.filter(item => item !== ids) : [...prev, ids],
    );
  };

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get(
          `/atendimentos/${id}/detalhe?ConIntegracao=0`,
        );

        const retorno: IReturnAgendamento = response.data;

        if (retorno) {
          // acrescentar telemedicina apenas para status 3 e 4 STATUS_TELEATENDIMENTO_STRING
          // and retorno.statusTelemedicina = 3 ou 4
          if (retorno.urlVideo && retorno.AgeTelemedicina) {
            const nome = user.UsuNome.split(' ')[0];
            setTelemedicina({
              urlVideo: `${retorno.urlVideo}&name=${nome}`,
              statusTelemedicina: retorno.statusTelemedicina,
            });
          }

          const {
            paciente: retornoPaciente,
            agenda: retornoAgenda,
            procedimentos: retornoProcedimentos,
            atendimento: retornoAtendimento,
            ...rest
          } = retorno;

          setAgendamento(rest);

          setAgenda(retornoAgenda);
          setProcedimentos(retornoProcedimentos);
          setAtendimento(retornoAtendimento);

          setPaciente({
            ...retornoPaciente,
            PacTelefone: formataTelefone(retornoPaciente.PacTelefone),
            PacCelular: formataTelefone(retornoPaciente.PacCelular),
            PacCPF: formataCPF(retornoPaciente.PacCPF),
            PacDataNascFormatada: parseISO(
              retornoPaciente.PacDataNasc,
            ).toLocaleDateString('pt-BR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            PacIdade: formataIdade(parseISO(retornoPaciente.PacDataNasc)),
            avatar_url: retornoPaciente.avatar_url || semFoto,
          });
        }
      } catch (error) {
        console.log(error);
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível buscar o agendamento',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    if (id && Number(id) !== 0) loadDados();
  }, [id, addToast, semFoto, user]);

  useEffect(() => {
    if (telemedicina) {
      setUrl(telemedicina.urlVideo);
      setIsOpen(true);
    }
  }, [setIsOpen, setUrl, telemedicina]);

  const configStatus = useMemo(() => {
    return teleStatusConfig[telemedicina?.statusTelemedicina || 1];
  }, [telemedicina]);

  const handleMudarStatus = useCallback(async () => {
    try {
      setLoading(true);

      const status = (telemedicina?.statusTelemedicina || 0) + 1;

      await api.patch<IRetornoMudarStatus>(
        `/teleatendimento/statusTelemedicina/${agendamento?.AgeChave}`,
        { status },
      );

      setUrl('');
      setIsOpen(false);
      history.push(`/agendaTeleAtendimento`);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [history, agendamento, telemedicina, setIsOpen, setUrl]);

  const handleEncaminharAtendimento = useCallback(async () => {
    try {
      setLoading(true);

      await api.patch(
        `/agendamentoantecipado/${agendamento?.AgeChave}/encaminharTelemedicina`,
      );

      setUrl('');
      setIsOpen(false);
      history.push(`/agendaTeleAtendimento`);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [history, agendamento, setIsOpen, setUrl]);

  return (
    <div className="min-vh-100 bg-body-tertiary">
      <main className="main-content">
        <div className="container-xxl py-4">
          {showNotice && (
            <div className="alert alert-success-subtle-custom border-success-subtle-custom d-flex align-items-start gap-3 mb-4 border-0 rounded-3">
              <div className="d-flex align-items-center justify-content-center rounded-circle p-1 bg-success-subtle flex-shrink-0">
                <FaCheck size={14} className="text-brand" />
              </div>
              <p className="mb-0 small lh-base">
                <strong>Atendimento encerrado.</strong> Confira as solicitações
                do médico e finalize o pós-atendimento do paciente.
              </p>
              <button
                type="button"
                className="btn btn-link ms-auto p-0 text-success-emphasis flex-shrink-0"
                onClick={() => setShowNotice(false)}
                aria-label="Fechar aviso"
              >
                <FaTimes size={16} />
              </button>
            </div>
          )}

          <div className="d-flex align-items-center flex-wrap gap-2 small text-cinza mb-4">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center gap-2 text-cinza"
            >
              <FaArrowLeft size={16} /> Voltar para fila
            </button>
            <FaChevronRight size={16} />
            <span className="fw-semibold text-body-emphasis">
              Atendimento #02481
            </span>
          </div>

          <div className="row g-4">
            <section className="col-12 col-lg-8 d-flex flex-column gap-4">
              <Card className="shadow-sm border h-100 rounded-5">
                <Card.Header
                  className="bg-white border-0 d-flex flex-column
                   flex-md-row align-items-start align-items-md-center
                   justify-content-between gap-3 p-4 pb-0"
                >
                  {agendamento !== undefined && paciente !== undefined && (
                    <>
                      <div>
                        <p className="small text-uppercase fw-bold text-cinza ls-14 mb-2">
                          Paciente em atendimento
                        </p>
                        <h2 className="card-title mb-0 fs-4 fw-bold">
                          {paciente.PacNome}
                        </h2>
                        <p className="text-cinza small mb-0 mt-1">
                          Consulta por telemedicina finalizada há 4 min
                        </p>
                      </div>
                      <span
                        className="badge rounded-pill d-flex align-items-center gap-2 border-0 px-3 py-2 flex-shrink-0"
                        style={{
                          backgroundColor: configStatus?.bg,
                          color: configStatus?.colorText,
                        }}
                      >
                        <i
                          className="rounded-circle mr-2"
                          style={{
                            width: 7,
                            height: 7,
                            backgroundColor: configStatus?.dot,
                          }}
                        />
                        {
                          STATUS_TELEATENDIMENTO_STRING[
                            telemedicina?.statusTelemedicina || 0
                          ]
                        }
                      </span>
                    </>
                  )}
                </Card.Header>
                <Card.Body className="p-4 pt-3">
                  <Row className="row-cols-1 row-cols-md-3 g-3 pt-3 mt-3 border-top mx-0">
                    <Col>
                      <p className="mb-1 small text-cinza">Idade</p>
                      <strong className="fw-semibold">
                        {paciente?.PacIdade}
                      </strong>
                    </Col>
                    <Col>
                      <p className="mb-1 small text-cinza">Telefone</p>
                      <strong className="fw-semibold d-flex align-items-center gap-2">
                        <FaPhone size={14} className="text-brand" />{' '}
                        {paciente?.PacCelular}
                      </strong>
                    </Col>
                    <Col>
                      <p className="mb-1 small text-cinza">
                        Data de nascimento
                      </p>
                      <strong className="fw-semibold">
                        {paciente?.PacDataNascFormatada}
                      </strong>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <div className="card shadow-sm border h-100 mt-2">
                <div className="card-header bg-white border-0 d-flex flex-column flex-md-row align-items-start align-items-md-end justify-content-between gap-3 p-4 pb-0">
                  <div>
                    <h2 className="fs-5 fw-bold mb-0">
                      Solicitações do médico
                    </h2>
                    <p className="text-cinza small mb-0 mt-1">
                      Selecione cada item após orientar ou entregar ao paciente.
                    </p>
                  </div>
                  <strong className="text-cinza small text-nowrap">
                    {completed.length}/{requests.length} concluídos
                  </strong>
                </div>
                <div className="card-body p-4">
                  <div className="d-flex flex-column gap-3">
                    {requests.map(request => {
                      const done = completed.includes(request.id);
                      const Icon = request.icon;
                      const bgStyle = done
                        ? 'bg-success-subtle text-brand'
                        : 'bg-body-secondary-subtle text-cinza';
                      const bgWhite = done
                        ? 'bg-success border-success text-white'
                        : 'border-secondary-subtle text-transparent';
                      return (
                        <button
                          type="button"
                          key={request.id}
                          onClick={() => toggleRequest(request.id)}
                          className={`btn d-flex align-items-center gap-3 p-3 text-start w-100 rounded-3 border ${bgStyle}`}
                        >
                          <div
                            className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 icon-40 ${bgStyle}`}
                          >
                            <Icon size={19} />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <strong className="d-block fw-semibold">
                              {request.label}
                            </strong>
                            <span className="d-block small text-truncate mt-1 text-cinza">
                              {request.detail}
                            </span>
                          </div>
                          <div
                            className={`d-flex align-items-center justify-content-center rounded-circle border flex-shrink-0 ${bgWhite}`}
                            style={{ width: 24, height: 24 }}
                          >
                            <FaCheck size={14} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            <aside className="col-12 col-lg-4 d-flex flex-column gap-4">
              <div className="card shadow-sm border ">
                <div className="card-body p-4 d-flex flex-column">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 icon-40"
                      style={{ background: '#f0eafa', color: '#8066b6' }}
                    >
                      <FaStethoscope size={19} />
                    </div>
                    <div>
                      <p className="mb-1 small text-cinza">Agenda</p>
                      <strong className="fw-semibold">{agenda?.ConNome}</strong>
                    </div>
                  </div>
                  {procedimentos.map(item => (
                    <div
                      key={`pro-${item.ProCodigo}`}
                      className="d-flex align-items-center gap-2 small text-cinza mt-3"
                    >
                      <i
                        className="rounded-circle bg-success mr-2"
                        style={{ width: 7, height: 7 }}
                      />
                      {item.ProNome}
                    </div>
                  ))}
                </div>
              </div>

              <div className="card border-0 text-white shadow bg-brand  mt-2">
                {telemedicina?.statusTelemedicina ===
                  STATUS_TELEATENDIMENTO.PRE_ATENDIMENTO && (
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <strong>{configStatus?.title || ''}</strong>
                      <FaPaperPlane size={16} className="text-white-50" />
                    </div>
                    <p className="text-white-50 small mt-3 mb-4 lh-base">
                      {configStatus?.text || ''}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleEncaminharAtendimento()}
                      className="btn btn-info  w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                    >
                      {configStatus?.title || ''}
                    </button>
                  </div>
                )}
                {telemedicina?.statusTelemedicina ===
                  STATUS_TELEATENDIMENTO.POS_ATENDIMENTO && (
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <strong>{configStatus?.title || ''}</strong>
                      <FaPaperPlane size={16} className="text-white-50" />
                    </div>
                    <p className="text-white-50 small mt-3 mb-4 lh-base">
                      {configStatus?.text || ''}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleEncaminharAtendimento()}
                      className="btn btn-info  w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                    >
                      {configStatus?.title || ''}
                    </button>
                  </div>
                )}
              </div>

              <div className="card border-dashed border-secondary-subtle bg-transparent  mt-2">
                <div className="card-body p-4 d-flex gap-3 text-cinza">
                  <FaPhone size={16} className="flex-shrink-0 mt-1 mr-2" />
                  <div>
                    <strong className="d-block text-body-emphasis fw-semibold small">
                      Precisa falar com o paciente?
                    </strong>
                    <p className="mb-0 small lh-base mt-1">
                      Entre em contato pelo telefone cadastrado para esclarecer
                      dúvidas.
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PacienteTeleAtendimento;
